import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import type {
  CashflowAccount,
  CashflowCategory,
  CashflowTransaction,
  CashflowType,
  CreateManualTransactionPayload,
  UpdateManualTransactionPayload,
} from '@/types/cashflow';

interface CashflowManualTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateManualTransactionPayload | UpdateManualTransactionPayload) => Promise<void>;
  transactionToEdit?: CashflowTransaction | null;
  accounts: CashflowAccount[];
  categories: CashflowCategory[];
  isLoading?: boolean;
}

export const CashflowManualTransactionModal: React.FC<CashflowManualTransactionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  transactionToEdit,
  accounts,
  categories,
  isLoading,
}) => {
  const [type, setType] = useState<CashflowType>('expense');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [categoryName, setCategoryName] = useState('');
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [accountId, setAccountId] = useState<number | ''>('');
  const [transactionDate, setTransactionDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const manualAccounts = accounts.filter(
    (acc) => !acc.is_sporto_wallet && acc.pocket_type !== 'sporto_wallet'
  );

  useEffect(() => {
    if (transactionToEdit) {
      setType(transactionToEdit.type || 'expense');
      setTitle(transactionToEdit.title || '');
      setAmount(transactionToEdit.amount || '');
      const existingCat = transactionToEdit.category_name || '';
      setCategoryName(existingCat);
      const isKnown = categories.some(
        (c) => c.name.toLowerCase() === existingCat.toLowerCase()
      );
      setIsCustomCategory(Boolean(existingCat && !isKnown));
      setAccountId(transactionToEdit.account_id || '');
      setTransactionDate(transactionToEdit.transaction_date || new Date().toISOString().split('T')[0]);
      setDescription(transactionToEdit.description || '');
    } else {
      setType('expense');
      setTitle('');
      setAmount('');
      setCategoryName('');
      setIsCustomCategory(false);
      setAccountId(manualAccounts.length > 0 ? manualAccounts[0].id : '');
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setDescription('');
    }
    setError(null);
  }, [transactionToEdit, isOpen, accounts, categories]);

  if (!isOpen) return null;

  const filteredCategories = categories.filter((c) => c.type === type);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Judul transaksi wajib diisi');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError('Nominal transaksi harus lebih dari Rp 0');
      return;
    }

    try {
      if (transactionToEdit) {
        await onSubmit({
          title: title.trim(),
          amount: Number(amount),
          category_name: categoryName.trim() || undefined,
          account_id: typeof accountId === 'number' ? accountId : undefined,
          transaction_date: transactionDate,
          description: description.trim() || undefined,
        });
      } else {
        await onSubmit({
          type,
          title: title.trim(),
          amount: Number(amount),
          category_name: categoryName.trim() || undefined,
          account_id: typeof accountId === 'number' ? accountId : undefined,
          transaction_date: transactionDate,
          description: description.trim() || undefined,
        });
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Gagal menyimpan transaksi');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl z-10 space-y-4 my-0 sm:my-8 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-200">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto sm:hidden" />
        <div className="flex items-center justify-between pb-2 border-b border-[#E0E6ED]">
          <div>
            <h3 className="text-base font-extrabold text-[#0E1726]">
              {transactionToEdit ? 'Edit Catatan Kas' : 'Catatan Kas Manual'}
            </h3>
            <p className="text-xs text-gray-400 font-medium">
              {transactionToEdit ? 'Perbarui detail transaksi' : 'Pemasukan atau pengeluaran di luar event'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-xs font-semibold text-rose-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* 1. Type Toggle (Pemasukan / Pengeluaran) - only on create */}
          {!transactionToEdit ? (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Tipe Transaksi
              </label>
              <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-2xl">
                <button
                  type="button"
                  onClick={() => {
                    setType('income');
                    setCategoryName('');
                    setIsCustomCategory(false);
                  }}
                  className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                    type === 'income'
                      ? 'bg-emerald-600 text-white shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon icon="ph:arrow-down-left-bold" className="w-3.5 h-3.5" />
                  <span>Pemasukan</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setType('expense');
                    setCategoryName('');
                    setIsCustomCategory(false);
                  }}
                  className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                    type === 'expense'
                      ? 'bg-rose-600 text-white shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon icon="ph:arrow-up-right-bold" className="w-3.5 h-3.5" />
                  <span>Pengeluaran</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500">Tipe:</span>
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold ${
                  type === 'income' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                }`}
              >
                {type === 'income' ? 'Pemasukan' : 'Pengeluaran'}
              </span>
            </div>
          )}

          {/* 2. Judul Transaksi */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Judul Transaksi <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              placeholder={type === 'income' ? 'Contoh: Iuran Bulanan Member' : 'Contoh: Beli Rompi & Peluit'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-sm text-[#0E1726] font-medium"
            />
          </div>

          {/* 3. Nominal */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Nominal (Rp) <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-2.5 text-sm font-bold text-gray-400">
                Rp
              </span>
              <input
                type="number"
                required
                min="1"
                placeholder="0"
                value={amount === '' ? '' : amount}
                onChange={(e) => {
                  const val = e.target.value;
                  setAmount(val === '' ? '' : Number(val));
                }}
                className="w-full pl-10 pr-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-sm text-[#0E1726] font-bold"
              />
            </div>
          </div>

          {/* 4. Kantong Kas */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Kantong Kas / Rekening
            </label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-xs text-[#0E1726] font-bold bg-white"
            >
              <option value="">Pilih Kantong Kas</option>
              {manualAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name}
                </option>
              ))}
            </select>
            {manualAccounts.length === 0 ? (
              <p className="text-[11px] text-amber-600 mt-1 font-medium">
                Belum ada kantong kas manual. Silakan buat kantong kas terlebih dahulu.
              </p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1 font-normal">
                Dompet Sporto khusus menampung pemasukan otomatis dari QRIS.
              </p>
            )}
          </div>

          {/* 5. Kategori & Tanggal Grid */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="flex items-center justify-between mb-1.5 min-h-[18px]">
                <label className="block text-xs font-bold text-gray-700">
                  Kategori
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const next = !isCustomCategory;
                    setIsCustomCategory(next);
                    if (!next) {
                      setCategoryName('');
                    }
                  }}
                  className="text-[11px] font-bold text-[#FC480D] hover:underline cursor-pointer flex items-center gap-0.5"
                  title={isCustomCategory ? 'Pilih dari daftar kategori' : 'Ketik kategori manual'}
                >
                  <Icon icon={isCustomCategory ? 'ph:list-bullets-bold' : 'ph:pencil-simple-bold'} className="w-3 h-3" />
                  <span>{isCustomCategory ? 'Pilih daftar' : 'Ketik manual'}</span>
                </button>
              </div>

              {isCustomCategory ? (
                <div>
                  <input
                    type="text"
                    list="cashflow-category-suggestions"
                    placeholder="Ketik kategori..."
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-xs text-[#0E1726] font-bold bg-white placeholder:font-normal placeholder:text-gray-400"
                    autoFocus
                  />
                  <datalist id="cashflow-category-suggestions">
                    {filteredCategories.map((c) => (
                      <option key={c.id} value={c.name} />
                    ))}
                  </datalist>
                </div>
              ) : (
                <select
                  value={categoryName}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setIsCustomCategory(true);
                      setCategoryName('');
                    } else {
                      setCategoryName(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-xs text-[#0E1726] font-bold bg-white"
                >
                  <option value="">Pilih Kategori</option>
                  {filteredCategories.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  <option value="__custom__" className="text-[#FC480D] font-bold">
                    + Ketik Kategori Lain...
                  </option>
                </select>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5 min-h-[18px]">
                <label className="block text-xs font-bold text-gray-700">
                  Tanggal
                </label>
              </div>
              <input
                type="date"
                required
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-xs text-[#0E1726] font-bold bg-white"
              />
            </div>
          </div>

          {/* 6. Deskripsi / Catatan */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Catatan (Opsional)
            </label>
            <textarea
              rows={2}
              placeholder="Keterangan tambahan transaksi"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-xs text-[#0E1726] font-medium resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-2xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 px-4 rounded-2xl bg-[#FC480D] text-white text-xs font-bold hover:bg-[#e03e08] transition-colors shadow-2xs disabled:opacity-50"
            >
              {isLoading ? 'Menyimpan...' : 'Simpan Transaksi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
